import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import cn from 'classnames';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';
import BaseCurrencyInput from 'src/shared/components/inputs/BaseCurrencyInput';

const handleStopWheel = (event) => {
  event?.stopPropagation();
};

/**
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<NumericTextBox>} TelerikNumericTextBoxProps
 *
 * @param {BaseFieldRenderProps & TelerikNumericTextBoxProps} fieldRenderProps
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const FormCurrencyInput = (fieldRenderProps, ref) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    useGrouping = true,
    wrapperClassName,
    optional,
    value,
    readOnly,
    stopWheel = true,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const labelId = label ? `${editorId}_label` : '';
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';

  const formatOptions = {
    style: 'decimal',
    useGrouping,
  };

  const innerRef = React.useRef(null);

  React.useImperativeHandle(ref, () => innerRef?.current);

  React.useEffect(() => {
    if (stopWheel) {
      innerRef.current?.element?.addEventListener('wheel', handleStopWheel);
    } else {
      innerRef.current?.element?.removeEventListener('wheel', handleStopWheel);
    }

    return () => {
      innerRef.current?.element?.removeEventListener('wheel', handleStopWheel);
    };
  }, [stopWheel]);

  if (!label) {
    return (
      <FieldWrapper className={cn(wrapperClassName, readOnly ? 'form-input-readonly' : '')}>
        <span data-testid={editorId}>
          <BaseCurrencyInput
            spinners={false}
            ariaLabelledBy={labelId}
            ariaDescribedBy={`${hintId} ${errorId}`}
            valid={valid}
            id={editorId}
            name={name}
            disabled={disabled}
            ref={innerRef}
            format={formatOptions}
            optional={optional}
            value={value}
            readOnly={readOnly}
            {...others}
          />
        </span>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper className={cn(wrapperClassName, readOnly ? 'form-input-readonly' : '')}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value}
        {...(readOnly ? { className: 'label-readonly' } : {})}
      >
        <span data-testid={editorId}>
          <BaseCurrencyInput
            spinners={false}
            ariaLabelledBy={labelId}
            ariaDescribedBy={`${hintId} ${errorId}`}
            valid={valid}
            id={editorId}
            name={name}
            disabled={disabled}
            ref={innerRef}
            format={formatOptions}
            optional={optional}
            value={value}
            readOnly={readOnly}
            {...others}
          />
        </span>
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

/**
 * A numeric textbox form field.
 *
 * @param {BaseFieldRenderProps & TelerikNumericTextBoxProps} fieldRenderProps
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Numeric textbox form field.
 */
const ForwardFormCurrencyInput = React.forwardRef(FormCurrencyInput);

export default ForwardFormCurrencyInput;